import { Component, signal } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ValidatorFn, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router, Params } from '@angular/router';
import { Alert, AlertsService, I18NService, UserService } from '@socrates/socrates-shared';
import { User } from '@socrates/common';

export const confirmPasswordValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
	const password = control.get('password');
	const confirm = control.get('confirmPassword');
	return password && confirm && password.value === confirm.value ? null : { 'passwordMismatch': true };
};

@Component({
	selector: 'password-modal',
	templateUrl: './newLogin.component.html',
	standalone: false,
})
export class NewLoginComponent {
	private _passwordForm: FormGroup;
	private _submitted = false;
	private username: string;
	private canceled = false;
	readonly submitting = signal(false);
	private readonly _placeholders: any;
	private _formErrors = {
		password: '',
		confirmPassword: ''
	};

	constructor(
		private alertsService: AlertsService,
		private userService: UserService,
		private router: Router,
		private route: ActivatedRoute,
		private formBuilder: FormBuilder
	) {
		this.route.queryParams.subscribe(
			(params: Params) => {
				this.username = params['username'];
				if (!this.username) {
					this.router.navigate(['/login']);
				}
			}
		);
		this._passwordForm = this.formBuilder.group(
			{
				password: ['', [Validators.required, Validators.minLength(8), Validators.pattern(/((?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W)){8,16}/)]],
				confirmPassword: ['', [Validators.required]]
			},
			{ validator: confirmPasswordValidator }
		);

		this._placeholders = {
			password: I18NService.getMessage('dashboard.password.placeholders.password'),
			confirm: I18NService.getMessage('dashboard.password.placeholders.confirm'),
		};
		this.alertsService.clearAlerts();
	}

	async onSubmit(): Promise<void> {
		if (!this.canceled) {
			if (this._passwordForm.valid) {
				this.submitting.set(true);
				const password: string = this._passwordForm.controls['password'].value as string;
				try {
					const user: User = await this.userService.firstLoginPasswordUpdate(this.username, password);
					this.submitting.set(false);
					if (user) {
						this.router.navigate(['/dashboard'], { state: { refreshWindow: true } });
					} else {
						this.alertsService.addAlert(new Alert({
							message: I18NService.getMessage('login.firstLogin.error.serverError'),
							alertType: 'error',
							namespace: '1stlg'
						}));
					}
				} catch (error: any) {
					this.submitting.set(false);
					if (error.status !== 401) {
						this.alertsService.addAlert(new Alert({
							message: I18NService.getMessage('login.firstLogin.error.serverError'),
							alertType: 'error',
							namespace: '1stlg'
						}));
					}
				}
			} else {
				this._submitted = true;
			}
		}
	}

	onCancel(): void {
		this.canceled = true;
		this.router.navigate(['/login']);
	}

	getMessage(key: string): string {
		return I18NService.getMessage(key);
	}

	get formControls() { return this._passwordForm.controls; }
	get passwordForm(): FormGroup { return this._passwordForm; }
	get submitted(): boolean { return this._submitted; }
	get formErrors(): any { return this._formErrors; }
	get placeholders(): any { return this._placeholders; }
}
